import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "./home.css";
import T from "./i18n";
import SEO from "./seo";
import PAGES from "./pages.json";

const TELEGRAM_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;
const TELEGRAM_API = `https://api.telegram.org/bot${TELEGRAM_TOKEN}/sendMessage`;

export const SITE_URL = "https://allsystems.uz";
export const GPS_URL = "https://gps.allsystems.uz";
export const LANGS = ["ru", "uz", "en"];
const OG_LOCALE = { ru: "ru_RU", uz: "uz_UZ", en: "en_US" };

const PHONE = "+998 93 099 60 60";
const PHONE_LINK = "tel:+998930996060";
const EMAIL = "allsystems.uz@gmail.com";

// TODO: вставьте ссылки на соцсети (пустая ссылка ведёт на форму заявки)
const SOCIALS = [
    { label: "Telegram", icon: "fa-brands fa-telegram", url: "" },
    { label: "Instagram", icon: "fa-brands fa-instagram", url: "" },
    { label: "Facebook", icon: "fa-brands fa-facebook-f", url: "" },
];

/* ============================================================
   DATA (language-independent)
   ============================================================ */

const NAV = [
    { href: "#services", key: "services" },
    { href: "#ai", key: "ai" },
    { href: "#process", key: "process" },
    { href: "#gps", key: "gps" },
    { href: "#contact", key: "contact" },
];

/* service card → its page, extra page tags and icon */
const SERVICES = [
    { key: "web", page: "websites", tags: ["landing", "ecommerce", "nukus_web"], icon: "fa-solid fa-laptop-code" },
    { key: "software", page: "software", tags: ["pos", "nukus_software"], icon: "fa-solid fa-cubes" },
    { key: "android", page: "android", tags: [], icon: "fa-brands fa-android" },
    { key: "crm", page: "crm", tags: ["erp", "nukus_crm"], icon: "fa-solid fa-diagram-project" },
    { key: "bitrix", page: "bitrix24", tags: ["amocrm"], icon: "fa-solid fa-address-card" },
    { key: "booking", page: "booking", tags: [], icon: "fa-solid fa-calendar-check" },
    { key: "integrations", page: "integrations", tags: ["payments"], icon: "fa-solid fa-plug" },
    { key: "wialon", page: "wialon", tags: [], icon: "fa-solid fa-satellite-dish" },
    { key: "bots", page: "telegram", tags: [], icon: "fa-brands fa-telegram" },
    { key: "ai", page: "ai", tags: ["automation"], icon: "fa-solid fa-brain" },
    { key: "iot", page: "arduino", tags: [], icon: "fa-solid fa-microchip" },
    { key: "seo", page: "seo", tags: ["nukus_seo"], icon: "fa-solid fa-magnifying-glass-chart" },
    { key: "support", page: "support", tags: [], icon: "fa-solid fa-headset" },
];
const SERVICE_BY_KEY = Object.fromEntries(SERVICES.map((s) => [s.key, s]));

const STACK = [
    ["fa-brands fa-react", "React"],
    ["fa-brands fa-node-js", "Node.js"],
    ["fa-brands fa-python", "Python"],
    ["fa-brands fa-android", "Android"],
    ["fa-solid fa-database", "PostgreSQL"],
    ["fa-brands fa-docker", "Docker"],
    ["fa-brands fa-linux", "Linux"],
    ["fa-solid fa-server", "Nginx"],
    ["fa-brands fa-telegram", "Telegram API"],
    ["fa-solid fa-address-card", "Bitrix24"],
    ["fa-solid fa-address-book", "amoCRM"],
    ["fa-solid fa-calculator", "1С"],
    ["fa-solid fa-satellite-dish", "Wialon"],
    ["fa-solid fa-credit-card", "Payme · Click · Uzum"],
    ["fa-solid fa-microchip", "Arduino / ESP32"],
    ["fa-solid fa-brain", "AI / LLM"],
    ["fa-brands fa-git-alt", "Git"],
];

/* integration hub nodes */
const HUB = [
    ["fa-solid fa-address-card", "Bitrix24"],
    ["fa-brands fa-telegram", "Telegram"],
    ["fa-solid fa-address-book", "amoCRM"],
    ["fa-solid fa-satellite-dish", "Wialon"],
    ["fa-solid fa-credit-card", "Payme"],
    ["fa-solid fa-calculator", "1С"],
    ["fa-solid fa-brain", "AI"],
    ["fa-solid fa-globe", "Website"],
];

const AI_ICONS = ["fa-solid fa-robot", "fa-solid fa-comments", "fa-solid fa-gears", "fa-solid fa-book-open"];
const WHY_ICONS = ["fa-solid fa-location-dot", "fa-solid fa-layer-group", "fa-solid fa-plug-circle-check", "fa-solid fa-truck-fast", "fa-solid fa-user-gear", "fa-solid fa-handshake"];
const GPS_ICONS = ["fa-solid fa-location-crosshairs", "fa-solid fa-gas-pump", "fa-solid fa-video", "fa-solid fa-screwdriver-wrench"];

/* Pages grouped for the footer (internal links help search engines find every page) */
const FOOTER_MORE = ["landing", "ecommerce", "pos", "erp", "amocrm", "payments", "automation", "arduino"];
const FOOTER_REGIONS = ["nukus_web", "nukus_software", "nukus_crm", "nukus_seo", "karakalpakstan", "urgench", "khorezm"];
const CITY_PAGES = ["karakalpakstan", "urgench", "khorezm"];
const RELATED_FALLBACK = ["websites", "crm", "telegram", "ai", "integrations", "nukus_web", "nukus_software"];

/* ============================================================
   HELPERS
   ============================================================ */

const pageByKey = (key) => PAGES.find((p) => p.key === key) || PAGES[0];
export const pagePath = (lang, key) => {
    const { slug } = pageByKey(key);
    return `/${lang}${slug ? `/${slug}` : ""}`;
};

/* "**bold**" → <strong>bold</strong> */
const Rich = ({ text }) =>
    String(text)
        .split(/(\*\*[^*]+\*\*)/g)
        .map((part, i) =>
            part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
        );

function setMeta(attr, name, content) {
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!data) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/* title, description, canonical, hreflang, Open Graph, <html lang>, structured data */
function useSeo(lang, page) {
    useEffect(() => {
        const seo = SEO[lang][page];
        const t = T[lang];
        const url = SITE_URL + pagePath(lang, page);

        document.documentElement.lang = lang;
        document.title = seo.title;
        setMeta("name", "description", seo.desc);
        setLink("canonical", url);
        LANGS.forEach((l) => setLink("alternate", SITE_URL + pagePath(l, page), l));
        setLink("alternate", SITE_URL + pagePath("ru", page), "x-default");

        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", "ALL SYSTEMS");
        setMeta("property", "og:title", seo.title);
        setMeta("property", "og:description", seo.desc);
        setMeta("property", "og:url", url);
        setMeta("property", "og:locale", OG_LOCALE[lang]);
        setMeta("property", "og:image", `${SITE_URL}/logo512.png`);

        setJsonLd("ld-org", {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "ALL SYSTEMS",
            url: SITE_URL + pagePath(lang, "home"),
            logo: `${SITE_URL}/logo512.png`,
            image: `${SITE_URL}/logo512.png`,
            description: SEO[lang].home.desc,
            telephone: "+998930996060",
            email: EMAIL,
            address: {
                "@type": "PostalAddress",
                addressLocality: lang === "en" ? "Nukus" : lang === "uz" ? "Nukus" : "Нукус",
                addressRegion: lang === "en" ? "Karakalpakstan" : lang === "uz" ? "Qoraqalpog‘iston" : "Каракалпакстан",
                addressCountry: "UZ",
            },
            areaServed: ["Nukus", "Karakalpakstan", "Urgench", "Khorezm", "Uzbekistan"],
            sameAs: [GPS_URL],
            openingHours: ["Mo-Fr 09:00-18:00", "Sa 09:00-12:30"],
        });

        setJsonLd(
            "ld-page",
            page === "home"
                ? {
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      mainEntity: t.faq.items.map(([q, a]) => ({
                          "@type": "Question",
                          name: q,
                          acceptedAnswer: { "@type": "Answer", text: a },
                      })),
                  }
                : {
                      "@context": "https://schema.org",
                      "@type": "BreadcrumbList",
                      itemListElement: [
                          { "@type": "ListItem", position: 1, name: t.home, item: SITE_URL + pagePath(lang, "home") },
                          { "@type": "ListItem", position: 2, name: seo.h1, item: url },
                      ],
                  }
        );
    }, [lang, page]);
}

/* ============================================================
   HOOKS & SMALL COMPONENTS
   ============================================================ */

function useReveal(threshold = 0.12) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [threshold]);

    return [ref, visible];
}

const Reveal = ({ as: Tag = "div", className = "", delay = 0, children, ...rest }) => {
    const [ref, visible] = useReveal();
    const classes = ["reveal", visible ? "in" : "", className].filter(Boolean).join(" ");
    const style = delay ? { transitionDelay: `${delay}ms`, ...rest.style } : rest.style;
    return (
        <Tag ref={ref} className={classes} {...rest} style={style}>
            {children}
        </Tag>
    );
};

const SectionHead = ({ eyebrow, title, text, light = false, center = false }) => (
    <Reveal className={`sec-head${center ? " center" : ""}${light ? " light" : ""}`}>
        <span className="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        {text && <p>{text}</p>}
    </Reveal>
);

/* Hero visual: code window, integration chips and a "new lead" toast */
const HeroVisual = ({ t }) => (
    <div className="hero-visual" aria-hidden="true">
        <div className="code-window">
            <div className="code-bar">
                <span className="dot r" />
                <span className="dot y" />
                <span className="dot g" />
                <em>business.config.js</em>
            </div>
            <pre className="code">
                <span className="ln"><b className="k">const</b> business = <b className="k">await</b> allsystems.<b className="f">build</b>({"{"}</span>
                <span className="ln">  site:  <b className="s">"allsystems.uz"</b>,</span>
                <span className="ln">  crm:   [<b className="s">"Bitrix24"</b>, <b className="s">"amoCRM"</b>],</span>
                <span className="ln">  bots:  telegram.<b className="f">connect</b>(),</span>
                <span className="ln">  pay:   [<b className="s">"Payme"</b>, <b className="s">"Click"</b>, <b className="s">"Uzum"</b>],</span>
                <span className="ln">  ai:    <b className="f">agent</b>({"{"} lang: [<b className="s">"ru"</b>, <b className="s">"uz"</b>] {"}"}),</span>
                <span className="ln">  gps:   wialon.<b className="f">sync</b>(),</span>
                <span className="ln">{"}"});</span>
                <span className="ln c">{t.hero.codeComment}</span>
                <span className="ln cursor-line"><i className="cursor" /></span>
            </pre>
        </div>

        <span className="chip-float cf1"><i className="fa-brands fa-telegram" /> Telegram</span>
        <span className="chip-float cf2"><i className="fa-solid fa-address-card" /> Bitrix24</span>
        <span className="chip-float cf3"><i className="fa-solid fa-brain" /> AI agent</span>
        <span className="chip-float cf4"><i className="fa-brands fa-android" /> Android</span>

        <div className="toast">
            <span className="toast-ico"><i className="fa-solid fa-bolt" /></span>
            <div>
                <b>{t.hero.toast}</b>
                <small>{t.hero.toastSub} <i className="fa-solid fa-check" /></small>
            </div>
        </div>
    </div>
);

/* Integration hub: services arranged around the logo, connected by animated lines */
const IntegrationHub = () => {
    const R = 42; // % radius
    return (
        <div className="hub" aria-hidden="true">
            <svg className="hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
                <circle cx="50" cy="50" r={R} className="hub-ring" />
                <circle cx="50" cy="50" r={R * 0.55} className="hub-ring inner" />
                {HUB.map((_, i) => {
                    const a = (i / HUB.length) * Math.PI * 2 - Math.PI / 2;
                    return (
                        <line
                            key={i}
                            x1="50"
                            y1="50"
                            x2={50 + R * Math.cos(a)}
                            y2={50 + R * Math.sin(a)}
                            className="hub-line"
                            style={{ animationDelay: `${-i * 0.35}s` }}
                        />
                    );
                })}
            </svg>
            <div className="hub-core">
                <img src="/logo192.png" alt="" />
            </div>
            {HUB.map(([icon, label], i) => {
                const a = (i / HUB.length) * Math.PI * 2 - Math.PI / 2;
                return (
                    <span
                        key={label}
                        className="hub-node"
                        style={{ left: `${50 + R * Math.cos(a)}%`, top: `${50 + R * Math.sin(a)}%`, animationDelay: `${i * 0.2}s` }}
                    >
                        <i className={icon} />
                        {label}
                    </span>
                );
            })}
        </div>
    );
};

/* ============================================================
   PAGE
   ============================================================ */

const Home = ({ lang = "ru", page = "home" }) => {
    const t = T[lang];
    const seo = SEO[lang][page];
    const pageInfo = pageByKey(page);
    const isHome = page === "home";
    const location = useLocation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [sending, setSending] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [sendError, setSendError] = useState(false);
    const [openFaq, setOpenFaq] = useState(0);

    useSeo(lang, page);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    const closeMenu = () => setMenuOpen(false);

    /* related links for the focus block */
    const related = (() => {
        const same = PAGES.filter((p) => p.svc && p.svc === pageInfo.svc && p.key !== page).map((p) => p.key);
        const list = [...same];
        if (pageInfo.svc && SERVICE_BY_KEY[pageInfo.svc]) {
            const main = SERVICE_BY_KEY[pageInfo.svc].page;
            if (main !== page && !list.includes(main)) list.unshift(main);
        }
        RELATED_FALLBACK.forEach((k) => {
            if (list.length < 6 && k !== page && !list.includes(k)) list.push(k);
        });
        return list.slice(0, 6);
    })();

    const isCity = page.startsWith("nukus_") || CITY_PAGES.includes(page);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.target;
        /* escape user input: the message is sent with parse_mode HTML */
        const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const data = Object.fromEntries([...new FormData(form).entries()].map(([k, v]) => [k, esc(v)]));
        const serviceName = data.service === "other" ? T.ru.contact.other : T.ru.services.items[data.service]?.[0] || "-";

        const text =
            `<b>🆕 Новая заявка с сайта allsystems.uz</b>\n\n` +
            `<b>🆔 Имя:</b> ${data.fullName || "-"}\n` +
            `<b>📲 Телефон:</b> ${data.phone || "-"}\n` +
            `<b>🛠 Услуга:</b> ${serviceName}\n` +
            `\n<b>💬 Задача:</b> ${data.message || "-"}\n\n` +
            `<b>🌐 Язык:</b> ${lang.toUpperCase()} · <b>📄 Страница:</b> ${location.pathname}`;

        setSending(true);
        setSendError(false);
        try {
            await axios.post(TELEGRAM_API, { chat_id: TELEGRAM_CHAT_ID, parse_mode: "HTML", text });
            setSubmitted(true);
            form.reset();
        } catch (err) {
            console.error("Telegram send failed:", err);
            setSendError(true);
        } finally {
            setSending(false);
        }
    };

    const LangSwitch = ({ className = "" }) => (
        <div className={`lang-switch ${className}`}>
            {LANGS.map((l) => (
                <Link
                    key={l}
                    to={pagePath(l, page)}
                    hrefLang={l}
                    lang={l}
                    title={T[l].langName}
                    className={l === lang ? "active" : ""}
                    onClick={closeMenu}
                >
                    {l.toUpperCase()}
                </Link>
            ))}
        </div>
    );

    const PageLink = ({ k, className = "" }) => (
        <Link to={pagePath(lang, k)} className={className}>
            {t.short[k]}
        </Link>
    );

    return (
        <>
            {/* ============ HEADER ============ */}
            <header className={`site-header${scrolled ? " scrolled" : ""}${menuOpen ? " menu-open" : ""}`}>
                <div className="wrap nav">
                    <Link to={pagePath(lang, "home")} className="logo" aria-label={`ALL SYSTEMS — ${t.home}`} onClick={closeMenu}>
                        <img src="/logo.png" alt="ALL SYSTEMS" />
                    </Link>

                    <nav className={`nav-links${menuOpen ? " open" : ""}`}>
                        {NAV.map((link) => (
                            <a key={link.href} href={link.href} onClick={closeMenu}>
                                {t.nav[link.key]}
                            </a>
                        ))}
                        <div className="nav-mobile-extra">
                            <LangSwitch />
                            <a href={PHONE_LINK} className="nav-mobile-phone">
                                <i className="fa-solid fa-phone" /> {PHONE}
                            </a>
                        </div>
                    </nav>

                    <div className="nav-cta">
                        <LangSwitch className="desktop-only" />
                        <a href={PHONE_LINK} className="nav-phone">
                            <i className="fa-solid fa-phone" />
                            <span>{PHONE}</span>
                        </a>
                        <a href="#contact" className="btn btn-primary btn-sm">
                            {t.cta}
                        </a>
                        <button
                            className={`burger${menuOpen ? " active" : ""}`}
                            aria-label={t.menu}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((o) => !o)}
                        >
                            <span />
                            <span />
                            <span />
                        </button>
                    </div>
                </div>
            </header>

            <main>
                {/* ============ HERO ============ */}
                <section className="hero">
                    <div className="hero-bg" aria-hidden="true">
                        <span className="blob b1" />
                        <span className="blob b2" />
                        <span className="grid-bg" />
                    </div>

                    <div className="wrap hero-grid">
                        <div className="hero-copy">
                            {isHome ? (
                                <span className="hero-badge">
                                    <i className="pulse" /> {t.hero.badge}
                                </span>
                            ) : (
                                <nav className="crumbs" aria-label="breadcrumb">
                                    <Link to={pagePath(lang, "home")}>{t.home}</Link>
                                    <i className="fa-solid fa-chevron-right" />
                                    <span>{t.short[page]}</span>
                                </nav>
                            )}

                            <h1 className="hero-title">{seo.h1}</h1>
                            <p className="hero-lead">{isHome ? t.hero.lead : seo.desc}</p>

                            <div className="hero-actions">
                                <a href="#contact" className="btn btn-primary">
                                    {t.hero.ctaPrimary} <i className="fa-solid fa-arrow-right" />
                                </a>
                                <a href={isHome ? "#services" : "#focus"} className="btn btn-ghost">
                                    {isHome ? t.hero.ctaSecondary : t.focus.includes}
                                </a>
                            </div>

                            <ul className="hero-trust">
                                {t.hero.trust.map((item) => (
                                    <li key={item}>
                                        <i className="fa-solid fa-circle-check" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <HeroVisual t={t} />
                    </div>

                    {/* tech marquee */}
                    <div className="stack" aria-label={t.stack}>
                        <p className="stack-label">{t.stack}</p>
                        <div className="marquee">
                            <div className="marquee-track">
                                {[...STACK, ...STACK].map(([icon, name], i) => (
                                    <span key={i} className="stack-item" aria-hidden={i >= STACK.length}>
                                        <i className={icon} /> {name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ FOCUS (subpages only) ============ */}
                {!isHome && (
                    <section className="focus" id="focus">
                        <div className="wrap focus-grid">
                            <Reveal className="focus-copy">
                                <span className="eyebrow">{isCity ? t.focus.eyebrowCity : t.focus.eyebrow}</span>
                                <h2>{t.short[page]}</h2>
                                <p>{seo.intro}</p>
                                <a href="#contact" className="btn btn-primary">
                                    {t.focus.cta} <i className="fa-solid fa-arrow-right" />
                                </a>
                            </Reveal>

                            <div className="focus-side">
                                <h3 className="focus-sub">{t.focus.includes}</h3>
                                <ul className="focus-points">
                                    {seo.points.map((p, i) => (
                                        <Reveal as="li" key={p} delay={i * 80}>
                                            <span className="num">0{i + 1}</span>
                                            {p}
                                        </Reveal>
                                    ))}
                                </ul>
                                <div className="focus-related">
                                    <span>{t.focus.related}:</span>
                                    {related.map((k) => (
                                        <PageLink key={k} k={k} className="pill" />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* ============ SERVICES ============ */}
                <section className="services" id="services">
                    <div className="wrap">
                        <SectionHead eyebrow={t.services.eyebrow} title={t.services.title} text={t.services.text} />

                        <div className="svc-grid">
                            {SERVICES.map((s, i) => {
                                const [title, text] = t.services.items[s.key];
                                const active = pageInfo.svc === s.key;
                                return (
                                    <Reveal
                                        as="article"
                                        key={s.key}
                                        delay={(i % 4) * 60}
                                        className={`svc-card${active ? " active" : ""}${s.key === "ai" ? " svc-ai" : ""}`}
                                    >
                                        <div className="svc-top">
                                            <span className="svc-ico">
                                                <i className={s.icon} />
                                            </span>
                                            <span className="svc-num">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <h3>
                                            <Link to={pagePath(lang, s.page)}>{title}</Link>
                                        </h3>
                                        <p>{text}</p>
                                        {s.tags.length > 0 && (
                                            <div className="svc-tags">
                                                {s.tags.map((k) => (
                                                    <PageLink key={k} k={k} />
                                                ))}
                                            </div>
                                        )}
                                        <Link to={pagePath(lang, s.page)} className="svc-more" aria-label={`${t.services.more}: ${title}`}>
                                            {t.services.more} <i className="fa-solid fa-arrow-right" />
                                        </Link>
                                    </Reveal>
                                );
                            })}
                            <Reveal className="svc-cta">
                                <div>
                                    <h3>{t.services.ctaTitle}</h3>
                                    <p>{t.services.ctaText}</p>
                                </div>
                                <a href="#contact" className="btn btn-primary">
                                    {t.cta} <i className="fa-solid fa-arrow-right" />
                                </a>
                            </Reveal>
                        </div>
                    </div>
                </section>

                {/* ============ AI ============ */}
                <section className="ai-sec" id="ai">
                    <div className="wrap ai-grid">
                        <div>
                            <SectionHead eyebrow={t.ai.eyebrow} title={t.ai.title} text={t.ai.text} light />
                            <div className="ai-cards">
                                {t.ai.cards.map(([title, text], i) => (
                                    <Reveal key={title} className="ai-card" delay={i * 70}>
                                        <i className={AI_ICONS[i]} />
                                        <h3>{title}</h3>
                                        <p>{text}</p>
                                    </Reveal>
                                ))}
                            </div>
                            <Link to={pagePath(lang, "ai")} className="link-arrow light">
                                {t.ai.more} <i className="fa-solid fa-arrow-right" />
                            </Link>
                        </div>

                        <Reveal className="chat" aria-hidden="true">
                            <div className="chat-head">
                                <span className="chat-ava">
                                    <i className="fa-solid fa-robot" />
                                </span>
                                <div>
                                    <b>ALL SYSTEMS AI</b>
                                    <small>
                                        <i className="pulse" /> {t.ai.status}
                                    </small>
                                </div>
                            </div>
                            <div className="chat-body">
                                {t.ai.chat.map(([who, msg], i) => (
                                    <p key={i} className={`msg ${who}`} style={{ animationDelay: `${0.3 + i * 0.9}s` }}>
                                        {msg}
                                    </p>
                                ))}
                                <span className="msg-system" style={{ animationDelay: `${0.3 + t.ai.chat.length * 0.9}s` }}>
                                    <i className="fa-solid fa-circle-check" /> {t.ai.system}
                                </span>
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* ============ PROCESS ============ */}
                <section className="process" id="process">
                    <div className="wrap">
                        <SectionHead eyebrow={t.process.eyebrow} title={t.process.title} center />
                        <ol className="steps">
                            {t.process.steps.map(([title, text], i) => (
                                <Reveal as="li" key={title} className="step" delay={i * 90}>
                                    <span className="step-num">{i + 1}</span>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </Reveal>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* ============ INTEGRATIONS ============ */}
                <section className="integr" id="integrations">
                    <div className="wrap integr-grid">
                        <div>
                            <SectionHead eyebrow={t.integr.eyebrow} title={t.integr.title} text={t.integr.text} />
                            <div className="integr-links">
                                {["integrations", "payments", "wialon", "bitrix24", "amocrm", "telegram"].map((k) => (
                                    <PageLink key={k} k={k} className="pill" />
                                ))}
                            </div>
                        </div>
                        <Reveal>
                            <IntegrationHub />
                        </Reveal>
                    </div>
                </section>

                {/* ============ GPS & CAMERAS (separate website) ============ */}
                <section className="gps-sec" id="gps">
                    <div className="wrap">
                        <Reveal className="gps-card">
                            <div className="gps-copy">
                                <span className="eyebrow">{t.gps.eyebrow}</span>
                                <h2>{t.gps.title}</h2>
                                <p>{t.gps.text}</p>
                                <a href={`${GPS_URL}/${lang}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                    {t.gps.cta} <i className="fa-solid fa-arrow-up-right-from-square" />
                                </a>
                            </div>
                            <ul className="gps-list">
                                {t.gps.items.map((item, i) => (
                                    <li key={item}>
                                        <span>
                                            <i className={GPS_ICONS[i]} />
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <svg className="gps-map" viewBox="0 0 400 240" aria-hidden="true">
                                <path className="gps-route" d="M20 200 C 90 190, 110 120, 170 130 S 260 60, 300 80 S 360 40, 385 30" />
                                <circle className="gps-dot" r="6">
                                    <animateMotion dur="7s" repeatCount="indefinite" path="M20 200 C 90 190, 110 120, 170 130 S 260 60, 300 80 S 360 40, 385 30" />
                                </circle>
                                <circle cx="20" cy="200" r="5" className="gps-pin" />
                                <circle cx="385" cy="30" r="5" className="gps-pin" />
                            </svg>
                        </Reveal>
                    </div>
                </section>

                {/* ============ GEOGRAPHY ============ */}
                <section className="geo" id="geo">
                    <div className="wrap">
                        <SectionHead eyebrow={t.geo.eyebrow} title={t.geo.title} text={t.geo.text} />
                        <div className="geo-grid">
                            {t.geo.cities.map(([key, name, note], i) => (
                                <Reveal key={key} delay={i * 70}>
                                    <Link to={pagePath(lang, key)} className={`geo-card${page === key ? " active" : ""}`}>
                                        <i className="fa-solid fa-location-dot" />
                                        <h3>{name}</h3>
                                        <p>{note}</p>
                                        <span className="geo-go">
                                            <i className="fa-solid fa-arrow-right" />
                                        </span>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                        <div className="geo-links">
                            {FOOTER_REGIONS.filter((k) => k.startsWith("nukus_")).map((k) => (
                                <PageLink key={k} k={k} className="pill" />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============ WHY US ============ */}
                <section className="why" id="why">
                    <div className="wrap">
                        <SectionHead eyebrow={t.why.eyebrow} title={t.why.title} />
                        <div className="why-grid">
                            {t.why.items.map(([title, text], i) => (
                                <Reveal key={title} className="why-item" delay={(i % 3) * 70}>
                                    <i className={WHY_ICONS[i]} />
                                    <div>
                                        <h3>{title}</h3>
                                        <p>{text}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============ FAQ ============ */}
                <section className="faq" id="faq">
                    <div className="wrap faq-grid">
                        <SectionHead eyebrow={t.faq.eyebrow} title={t.faq.title} />
                        <div className="faq-list">
                            {t.faq.items.map(([q, a], i) => {
                                const open = openFaq === i;
                                return (
                                    <div key={q} className={`faq-item${open ? " open" : ""}`}>
                                        <h3>
                                            <button
                                                type="button"
                                                aria-expanded={open}
                                                aria-controls={`faq-a-${i}`}
                                                id={`faq-q-${i}`}
                                                onClick={() => setOpenFaq(open ? -1 : i)}
                                            >
                                                {q}
                                                <i className="fa-solid fa-plus" />
                                            </button>
                                        </h3>
                                        <div className="faq-a" id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`}>
                                            <p>
                                                <Rich text={a} />
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* ============ CONTACT ============ */}
                <section className="contact" id="contact">
                    <div className="wrap contact-grid">
                        <div className="contact-info">
                            <SectionHead eyebrow={t.contact.eyebrow} title={t.contact.title} text={t.contact.text} light />
                            <ul className="contact-list">
                                <li>
                                    <span><i className="fa-solid fa-phone" /></span>
                                    <div>
                                        <small>{t.contact.phoneTitle}</small>
                                        <a href={PHONE_LINK}>{PHONE}</a>
                                    </div>
                                </li>
                                <li>
                                    <span><i className="fa-solid fa-envelope" /></span>
                                    <div>
                                        <small>{t.contact.emailTitle}</small>
                                        <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                                    </div>
                                </li>
                                <li>
                                    <span><i className="fa-solid fa-location-dot" /></span>
                                    <div>
                                        <small>{t.contact.addressTitle}</small>
                                        <b>{t.contact.addressValue}</b>
                                    </div>
                                </li>
                                <li>
                                    <span><i className="fa-solid fa-clock" /></span>
                                    <div>
                                        <small>{t.contact.hours}</small>
                                        {t.contact.days.map(([d, h]) => (
                                            <b key={d} className="hours-row">
                                                <em>{d}</em> {h}
                                            </b>
                                        ))}
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div className="form-card">
                            {submitted ? (
                                <div className="form-success">
                                    <span><i className="fa-solid fa-check" /></span>
                                    <h3>{t.contact.successTitle}</h3>
                                    <p>{t.contact.successText}</p>
                                    <button type="button" className="btn btn-ghost" onClick={() => setSubmitted(false)}>
                                        {t.contact.again}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <div className="field">
                                        <label htmlFor="f-name">{t.contact.labels.name}</label>
                                        <input id="f-name" name="fullName" required placeholder={t.contact.placeholders.name} autoComplete="name" />
                                    </div>
                                    <div className="field">
                                        <label htmlFor="f-phone">{t.contact.labels.phone}</label>
                                        <input id="f-phone" name="phone" type="tel" required placeholder={t.contact.placeholders.phone} autoComplete="tel" />
                                    </div>
                                    <div className="field">
                                        <label htmlFor="f-service">{t.contact.labels.service}</label>
                                        <div className="select-wrap">
                                            <select id="f-service" name="service" key={page} defaultValue={pageInfo.svc || "web"}>
                                                {SERVICES.map((s) => (
                                                    <option key={s.key} value={s.key}>
                                                        {t.services.items[s.key][0]}
                                                    </option>
                                                ))}
                                                <option value="other">{t.contact.other}</option>
                                            </select>
                                            <i className="fa-solid fa-chevron-down" />
                                        </div>
                                    </div>
                                    <div className="field">
                                        <label htmlFor="f-msg">{t.contact.labels.message}</label>
                                        <textarea id="f-msg" name="message" rows="4" placeholder={t.contact.placeholders.message} />
                                    </div>
                                    {sendError && <p className="form-error">{t.contact.error}</p>}
                                    <button type="submit" className="btn btn-primary btn-block" disabled={sending}>
                                        {sending ? t.contact.sending : t.contact.send} {!sending && <i className="fa-solid fa-paper-plane" />}
                                    </button>
                                    <p className="consent">{t.contact.consent}</p>
                                </form>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            {/* ============ FOOTER ============ */}
            <footer className="site-footer">
                <div className="wrap">
                    <div className="footer-top">
                        <div className="footer-brand">
                            <Link to={pagePath(lang, "home")} className="logo">
                                <img src="/logo-light.png" alt="ALL SYSTEMS" />
                            </Link>
                            <p>{t.footer.about}</p>
                            <div className="socials">
                                {SOCIALS.map((s) => (
                                    <a key={s.label} href={s.url || "#contact"} aria-label={s.label} {...(s.url ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                                        <i className={s.icon} />
                                    </a>
                                ))}
                            </div>
                            <LangSwitch className="lang-switch-dark" />
                        </div>

                        <nav className="footer-col" aria-label={t.footer.services}>
                            <h4>{t.footer.services}</h4>
                            <ul>
                                {SERVICES.map((s) => (
                                    <li key={s.key}>
                                        <PageLink k={s.page} />
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <nav className="footer-col" aria-label={t.footer.more}>
                            <h4>{t.footer.more}</h4>
                            <ul>
                                {FOOTER_MORE.map((k) => (
                                    <li key={k}>
                                        <PageLink k={k} />
                                    </li>
                                ))}
                            </ul>
                            <h4 className="mt">{t.footer.company}</h4>
                            <ul>
                                <li><PageLink k="about" /></li>
                                <li><PageLink k="contacts" /></li>
                            </ul>
                        </nav>

                        <nav className="footer-col" aria-label={t.footer.regions}>
                            <h4>{t.footer.regions}</h4>
                            <ul>
                                {FOOTER_REGIONS.map((k) => (
                                    <li key={k}>
                                        <PageLink k={k} />
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className="footer-col">
                            <h4>{t.nav.contact}</h4>
                            <ul className="footer-contacts">
                                <li><a href={PHONE_LINK}><i className="fa-solid fa-phone" /> {PHONE}</a></li>
                                <li><a href={`mailto:${EMAIL}`}><i className="fa-solid fa-envelope" /> {EMAIL}</a></li>
                                <li><span><i className="fa-solid fa-location-dot" /> {t.contact.addressValue.split(",")[0]}</span></li>
                            </ul>
                            <a href={`${GPS_URL}/${lang}`} target="_blank" rel="noopener noreferrer" className="footer-gps">
                                <i className="fa-solid fa-satellite-dish" />
                                <span>
                                    <small>{t.footer.gps}</small>
                                    <b>gps.allsystems.uz</b>
                                </span>
                            </a>
                        </div>
                    </div>

                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} {t.footer.copy}</p>
                        <p>allsystems.uz · gps.allsystems.uz</p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Home;
